import React, { useEffect, useState } from 'react';
import { FaCreditCard, FaPhone, FaUser } from 'react-icons/fa';
import { motion } from 'framer-motion';
import withdrawalService from '../../services/withdrawalService';
import paymentService from '../../services/paymentService';
import { useToast } from '../Shared/Toast';

const formatRs = (value) => `Rs ${Number(value || 0).toFixed(2)}`;

const WithdrawalForm = ({ contestId, fantasyTeamId, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState({
    amount: '',
    bankName: 'JazzCash',
    accountNumber: '',
    accountHolderName: '',
    phoneNumber: ''
  });
  const [walletSummary, setWalletSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(true);
  const [formError, setFormError] = useState('');
  const { success, error } = useToast();

  useEffect(() => {
    const loadWalletSummary = async () => {
      try {
        setWalletLoading(true);
        const data = await paymentService.getWalletSummary();
        setWalletSummary(data);
      } catch (loadError) {
        const message = loadError.response?.data || loadError.message || 'Failed to load wallet summary.';
        setFormError(message);
      } finally {
        setWalletLoading(false);
      }
    };

    loadWalletSummary();
  }, []);

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setFormError('');

    const amount = Number(formData.amount);
    const minimumWithdrawalAmount = Number(walletSummary?.minimumWithdrawalAmount || 2500);
    const withdrawableAmount = Number(walletSummary?.withdrawableAmount || 0);

    if (!amount || amount <= 0) {
      setFormError('Please enter a valid withdrawal amount.');
      return;
    }

    if (amount < minimumWithdrawalAmount) {
      setFormError(`Minimum withdrawal amount is ${formatRs(minimumWithdrawalAmount)}.`);
      return;
    }

    if (amount > withdrawableAmount) {
      setFormError('Requested amount exceeds your withdrawable balance.');
      return;
    }

    try {
      setLoading(true);
      const payload = {
        contestId: contestId ? Number(contestId) : null,
        fantasyTeamId: fantasyTeamId ? Number(fantasyTeamId) : null,
        amount,
        bankName: formData.bankName,
        accountNumber: formData.accountNumber,
        accountHolderName: formData.accountHolderName,
        phoneNumber: formData.phoneNumber
      };

      const result = await withdrawalService.createWithdrawalRequest(payload);
      success('Withdrawal Requested', 'Your request has been submitted for admin review.');
      window.dispatchEvent(new Event('balanceUpdated'));
      onSuccess(result);
    } catch (submitError) {
      const message = submitError.response?.data || submitError.message || 'Failed to submit withdrawal request.';
      setFormError(message);
      error('Withdrawal Failed', message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25, delay: 0.05 }}
        className="w-full max-w-lg rounded-xl bg-white shadow-2xl"
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Request Withdrawal</h3>
              <p className="mt-1 text-sm text-gray-500">Withdraw from your wallet balance.</p>
            </div>
            <button
              onClick={onCancel}
              className="text-gray-400 transition-colors hover:text-gray-700"
              type="button"
            >
              ✕
            </button>
          </div>
        </div>

        <div className="px-6 py-5">
          <div className="mb-5 rounded-lg border border-emerald-100 bg-emerald-50 p-4">
            {walletLoading ? (
              <p className="text-sm text-emerald-700">Loading wallet...</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-emerald-700">Playable</p>
                  <p className="text-lg font-bold text-gray-900">{formatRs(walletSummary?.totalPlayableAmount)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-emerald-700">Withdrawable</p>
                  <p className="text-lg font-bold text-gray-900">{formatRs(walletSummary?.withdrawableAmount)}</p>
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wide text-emerald-700">Minimum</p>
                  <p className="text-lg font-bold text-gray-900">{formatRs(walletSummary?.minimumWithdrawalAmount || 2500)}</p>
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">Withdrawal Amount</label>
              <input
                type="number"
                name="amount"
                value={formData.amount}
                onChange={handleInputChange}
                min={walletSummary?.minimumWithdrawalAmount || 2500}
                step="0.01"
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter amount"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                <FaCreditCard className="mr-2 inline" />
                Withdrawal Method
              </label>
              <select
                name="bankName"
                value={formData.bankName}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-indigo-500"
              >
                <option value="JazzCash">JazzCash</option>
                <option value="EasyPaisa">EasyPaisa</option>
                <option value="Bank Transfer">Bank Transfer</option>
              </select>
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                <FaUser className="mr-2 inline" />
                Account Holder Name
              </label>
              <input
                type="text"
                name="accountHolderName"
                value={formData.accountHolderName}
                onChange={handleInputChange}
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter account holder name"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                <FaCreditCard className="mr-2 inline" />
                Account Number
              </label>
              <input
                type="text"
                name="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter account number"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-medium text-gray-700">
                <FaPhone className="mr-2 inline" />
                Phone Number
              </label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                required
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:border-transparent focus:ring-2 focus:ring-indigo-500"
                placeholder="Enter phone number"
              />
            </div>

            {formError && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                {formError}
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onCancel}
                className="flex-1 rounded-lg bg-gray-200 px-4 py-3 font-medium text-gray-700 transition-colors hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading || walletLoading}
                className="flex-1 rounded-lg bg-indigo-600 px-4 py-3 font-medium text-white transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Request'}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default WithdrawalForm;
